import { useEffect, useState, useCallback } from 'react';

import { Grid, Typography } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { Product } from '@/types/menuStore.types';
import { CustomTableMeta } from '@/components/Tables/TableCard/TableCard.types';
import Input from '@/components/Input/Input';
import { InputStates } from '@/components/Input/Input.types';
import styles from './CellEditProductPrice.module.css';

export const CellEditProductPrice = ({ getValue, row, column, table }: CellContext<Product, unknown>) => {
  const columnMeta = column?.columnDef?.meta;
  const initialValue = getValue<string>();
  const { meta } = table.options;
  const { editedRows, cancelEdition, setCancelEdition, updateData, revertData, setEditedRows, loadingData, userType } =
    meta as CustomTableMeta<Product>;

  const { index: rowIndex } = row;
  const editing = !!(editedRows && editedRows[Number(row.id)]);

  const [value, setValue] = useState(initialValue);

  const [state, setState] = useState(InputStates.Default);

  const isValidInput = (inputValue: string) => {
    const validate = columnMeta?.validate;
    if (userType === 'Admin') {
      return true;
    }
    return validate ? validate(Number(inputValue), row.original) : true;
  };

  const rangeText = useCallback(() => {
    const product = row.original;
    const percentage = product.range || 0;

    const limSup = product.basePrice + percentage * product.basePrice;
    const limInf = product.basePrice - percentage * product.basePrice;

    return `${limInf.toFixed(2)} to ${limSup.toFixed(2)}`;
  }, [row.original.range, row.original.basePrice]);

  const handleOnChangeInput = (inputValue: string) => {
    const isValid = isValidInput(inputValue);
    if (isValid) {
      setValue(inputValue);
      setState(InputStates.Success);
      updateData(row.index, column.id, inputValue);
    } else {
      updateData(row.index, column.id, value);
      setState(InputStates.Error);
    }
  };
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (cancelEdition && cancelEdition[rowIndex] === true) {
      handleOnChangeInput(initialValue); // need to set the original value when the user cancel the operation
    }
  }, [cancelEdition]);

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="end"
      sx={{
        paddingTop: '0px',
        m: 0,
      }}
    >
      {!editing && (
        <Grid
          item
          xs={2}
          sx={{
            minWidth: '240px',
          }}
        >
          ${loadingData ? '...' : (+value || 0).toFixed(2)}
        </Grid>
      )}

      {/* nedds validate */}
      {editing && (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
          sx={{
            minWidth: '340px',
          }}
        >
          <Grid
            item
            alignItems="center"
            justifyContent="flex-start"
            xs={2}
            sx={{
              minWidth: '100px',
            }}
          >
            <Input
              className={styles.cellEditPriceInput}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  if (revertData) {
                    revertData(rowIndex, true);
                  }
                  if (setEditedRows) {
                    setEditedRows((old: Record<string, unknown>[]) => ({
                      ...old,
                      [row.id]: !old[Number(row.id)],
                    }));
                  }

                  if (setCancelEdition) {
                    setCancelEdition((old: Record<string, unknown>[]) => ({
                      ...old,
                      [row.id]: true,
                    }));
                  }
                }
              }}
              type="number"
              step="0.1"
              state={state}
              value={value}
              onChange={(e) => handleOnChangeInput(e.target.value)}
              label=""
              id=""
              data-testid="input-price"
            />
          </Grid>
          <Grid item xs="auto" mb={1}>
            <Typography
              mt={2}
              sx={{
                fontWeight: '400',
                fontSize: '12px',
                color: `${state === InputStates.Error && userType !== 'Admin' ? 'red' : ''}`,
              }}
            >
              Price range: {rangeText()}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
